import React, { useState } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  MapPinIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayCircleIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { useContent } from '../ContentContext';

// ── Types ─────────────────────────────────────────────────────────────────────
interface ProjectMedia {
  id: number;
  media_type: 'image' | 'video';
  url: string;
  caption?: string;
  sort_order?: number;
}

interface Project {
  id: number;
  name: string;
  location: string;
  tagline?: string;
  description?: string;
  image_url?: string;
  brochure_url?: string;
  status?: string;
  video_url?: string;
  highlights?: string;
  price_range?: string;
  plot_sizes?: string;
  area?: string;
  media?: ProjectMedia[];
  // legacy keys
  image?: string;
  brochure?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  active:    { label: 'Available',  cls: 'bg-green-100 text-green-700 border border-green-200' },
  upcoming:  { label: 'Upcoming',   cls: 'bg-blue-100 text-blue-700 border border-blue-200'   },
  completed: { label: 'Sold Out',   cls: 'bg-gray-100 text-gray-600 border border-gray-200'   },
};

const isEmbedUrl = (url: string) =>
  url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');

const toEmbedUrl = (url: string): string => {
  const m = url.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return url;
};

// ── Project Detail Modal ──────────────────────────────────────────────────────
const ProjectModal: React.FC<{ project: Project; onClose: () => void }> = ({ project, onClose }) => {
  const coverImg = project.image_url || project.image || '';
  const allImages: string[] = [
    ...(coverImg ? [coverImg] : []),
    ...(project.media || []).filter(m => m.media_type === 'image').map(m => m.url),
  ];
  const videos = [
    ...(project.video_url ? [{ url: project.video_url, caption: 'Project Video' }] : []),
    ...(project.media || []).filter(m => m.media_type === 'video').map(m => ({ url: m.url, caption: m.caption || 'Video' })),
  ];

  const [imgIdx, setImgIdx] = useState(0);
  const statusInfo = STATUS_BADGE[project.status || 'active'] || STATUS_BADGE.active;
  const highlightsList = project.highlights
    ? project.highlights.split('|').map(h => h.trim()).filter(Boolean)
    : [];
  const brochureUrl = project.brochure_url || project.brochure || '';

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative bg-white rounded-2xl sm:rounded-[2rem] w-full max-w-5xl max-h-[95vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          title="Close"
          aria-label="Close project details"
          className="absolute top-3 right-3 sm:top-5 sm:right-5 z-20 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all hover:scale-110"
        >
          <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
        </button>

        {/* Hero Image Carousel */}
        {allImages.length > 0 ? (
          <div className="relative w-full h-56 sm:h-[420px] bg-gray-900 rounded-t-2xl sm:rounded-t-[2rem] overflow-hidden">
            <img
              src={allImages[imgIdx]}
              alt={project.name}
              className="w-full h-full object-cover transition-opacity duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end p-4 sm:p-8">
              <div>
                <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-2 sm:mb-3 ${statusInfo.cls}`}>
                  {statusInfo.label}
                </span>
                <h2 className="text-xl sm:text-3xl font-extrabold text-white leading-tight">{project.name}</h2>
                {project.location && (
                  <p className="text-white/80 flex items-center gap-1 mt-1 text-xs sm:text-sm">
                    <MapPinIcon className="w-4 h-4" /> {project.location}
                  </p>
                )}
              </div>
            </div>
            {allImages.length > 1 && (
              <>
                <button
                  onClick={() => setImgIdx(i => (i - 1 + allImages.length) % allImages.length)}
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setImgIdx(i => (i + 1) % allImages.length)}
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {allImages.map((_, i) => (
                    <button
                      key={i}
                      aria-label={`Go to image ${i + 1}`}
                      onClick={() => setImgIdx(i)}
                      className={`rounded-full transition-all ${i === imgIdx ? 'bg-white w-5 h-2' : 'bg-white/50 w-2 h-2'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="rounded-t-2xl sm:rounded-t-[2rem] bg-gradient-to-br from-gray-800 to-gray-900 h-20 sm:h-24 flex items-center px-4 sm:px-8">
            <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${statusInfo.cls}`}>
              {statusInfo.label}
            </span>
          </div>
        )}

        {/* Thumbnail Strip */}
        {allImages.length > 1 && (
          <div className="flex gap-2 px-4 sm:px-6 pt-3 sm:pt-4 overflow-x-auto no-scrollbar">
            {allImages.map((img, i) => (
              <button
                key={i}
                aria-label={`View image ${i + 1}`}
                onClick={() => setImgIdx(i)}
                className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl overflow-hidden border-2 transition-all ${
                  i === imgIdx ? 'border-[#EF5D22] opacity-100' : 'border-transparent opacity-55 hover:opacity-90'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="p-4 sm:p-6 lg:p-8 grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Left — description + highlights */}
          <div className="space-y-5">
            {project.tagline && (
              <p className="text-[#EF5D22] font-bold text-lg italic">"{project.tagline}"</p>
            )}
            {project.description && (
              <p className="text-gray-600 leading-relaxed text-sm">{project.description}</p>
            )}
            {highlightsList.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-900 mb-3 text-base">Key Highlights</h4>
                <ul className="space-y-2">
                  {highlightsList.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                      <CheckBadgeIcon className="w-5 h-5 text-[#EF5D22] shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right — specs + actions */}
          <div className="space-y-5">
            {(project.price_range || project.plot_sizes || project.area) && (
              <div className="grid grid-cols-2 gap-3">
                {project.price_range && (
                  <div className="bg-orange-50 rounded-2xl p-4 border border-orange-100 col-span-2">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Price Range</p>
                    <p className="font-extrabold text-[#EF5D22] text-xl">{project.price_range}</p>
                  </div>
                )}
                {project.plot_sizes && (
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Plot Sizes</p>
                    <p className="font-extrabold text-gray-800">{project.plot_sizes}</p>
                  </div>
                )}
                {project.area && (
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Total Area</p>
                    <p className="font-extrabold text-gray-800">{project.area}</p>
                  </div>
                )}
              </div>
            )}

            {brochureUrl && (
              <a
                href={brochureUrl}
                download
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center justify-center gap-2 w-full bg-[#EF5D22] hover:bg-[#d9541a] text-white font-bold py-4 rounded-xl shadow-lg transition-all hover:scale-[1.02]"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
                Download Brochure
              </a>
            )}
          </div>
        </div>

        {/* Videos Section */}
        {videos.length > 0 && (
          <div className="px-6 sm:px-8 pb-8">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2 text-base">
              <PlayCircleIcon className="w-5 h-5 text-[#EF5D22]" />
              Videos
            </h4>
            <div className="grid sm:grid-cols-2 gap-4">
              {videos.map((v, i) => (
                <div key={i} className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                  {isEmbedUrl(v.url) ? (
                    <iframe
                      src={toEmbedUrl(v.url)}
                      className="w-full aspect-video"
                      allowFullScreen
                      title={v.caption || `Video ${i + 1}`}
                    />
                  ) : (
                    <video src={v.url} controls className="w-full aspect-video bg-black" />
                  )}
                  {v.caption && (
                    <p className="text-xs text-gray-500 text-center py-2 px-4">{v.caption}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Project Card ──────────────────────────────────────────────────────────────
const ProjectCard: React.FC<{ project: Project; onOpen: () => void }> = ({ project, onOpen }) => {
  const statusInfo = STATUS_BADGE[project.status || 'active'] || STATUS_BADGE.active;
  const coverImg = project.image_url || project.image || '/images/hero.jpeg';
  const extraMedia = (project.media?.length || 0) + (project.video_url ? 1 : 0);

  return (
    <div
      className="bg-white rounded-2xl sm:rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 group border border-gray-100 cursor-pointer"
      onClick={onOpen}
    >
      <div className="relative h-48 sm:h-72 overflow-hidden">
        <img
          src={coverImg}
          alt={project.name}
          className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
        />
        <span className={`absolute top-3 sm:top-4 left-3 sm:left-4 text-xs font-bold px-3 py-1 rounded-full ${statusInfo.cls}`}>
          {statusInfo.label}
        </span>
        {extraMedia > 0 && (
          <span className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-black/60 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
            <PlayCircleIcon className="w-3.5 h-3.5" />
            {extraMedia} media
          </span>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4 sm:p-6">
          {project.tagline && (
            <p className="text-white/90 text-sm font-medium italic">"{project.tagline}"</p>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-6 flex justify-between items-center">
        <div className="min-w-0">
          <h4 className="font-extrabold text-lg sm:text-xl text-gray-900 group-hover:text-[#EF5D22] transition-colors leading-tight truncate">
            {project.name}
          </h4>
          {project.location && (
            <p className="text-xs sm:text-sm text-gray-400 mt-0.5 flex items-center gap-1">
              <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{project.location}</span>
            </p>
          )}
          {project.price_range && (
            <p className="text-xs font-bold text-[#EF5D22] mt-1">{project.price_range}</p>
          )}
        </div>
        <div className="ml-3 sm:ml-4 w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-100 flex items-center justify-center group-hover:bg-[#EF5D22] group-hover:border-[#EF5D22] group-hover:text-white text-gray-500 transition-all duration-300 shrink-0">
          <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
      </div>
    </div>
  );
};

// ── Main Projects Component ───────────────────────────────────────────────────
const Projects = () => {
  const { content } = useContent();
  const proj = (content as any).projects || { items: [] };
  const [openProject, setOpenProject] = useState<Project | null>(null);

  const badge      = proj.badge      || 'Our Projects';
  const titlePart1 = proj.titlePart1 || 'Explore Our';
  const titlePart2 = proj.titlePart2 || 'Landmark Developments';

  return (
    <section id="projects" className="py-12 sm:py-24 px-4 sm:px-10 bg-gray-50">
      <div className="container mx-auto">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-10 sm:mb-16 gap-4 sm:gap-6">
          <div>
            <span className="text-[#EF5D22] font-bold tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4 block">
              {badge}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
              {titlePart1}{' '}
              <span className="text-[#EF5D22]">{titlePart2}</span>
            </h2>
          </div>
          <p className="text-gray-500 max-w-xs text-xs sm:text-sm leading-relaxed">
            Click any project card to explore images, videos, highlights and download the brochure.
          </p>
        </div>

        {/* Project Grid */}
        {proj.items.length === 0 ? (
          <div className="text-center py-16 sm:py-24 text-gray-400">
            <p className="text-lg sm:text-xl font-medium">No projects added yet.</p>
            <p className="text-sm mt-2">Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {proj.items.map((project: Project) => (
              <ProjectCard
                key={project.id || project.name}
                project={project}
                onOpen={() => setOpenProject(project)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {openProject && (
        <ProjectModal project={openProject} onClose={() => setOpenProject(null)} />
      )}
    </section>
  );
};

export default Projects;
